// ─────────────────────────────────────────────────────────────────────────
// Faithful port of the web KYRS auto-scheduler engine.
//
// Web references (read completely before touching this file):
//   src/app/tasks/[listId]/page.tsx  → initiateAutoSchedulingForList
//     (triggers: task add / done-toggle / delete, always re-optimizes
//      every eligible undone task in the list)
//   src/app/tasks/page.tsx           → initiateAutoScheduling +
//                                       handleForceRescheduleList
//     (triggers: per-list "Auto-Scheduler" toggle turned ON, and the
//      "Reschedule" button — only targets tasks that are not already
//      scheduled, or an explicit eligible-id subset)
//   src/app/journal/page.tsx         → handleItineraryItemReschedule
//     (trigger: Journal "Started"/"Reschedule" itinerary actions — uses
//      the TASK'S OWN group, not the list default, and searches starting
//      tomorrow, not today)
//
// Same tables/columns as web, always user-scoped:
//   tasks: scheduled ('yyyy-MM-dd HH:mm'), scheduled_manual, title, etc,
//          group_id/sg1_id/sg2_id, value_selections, priority_add,
//          category, unlisted, parent_task_id, tabled, order
//   task_lists: auto_scheduler_enabled, default_group_id, default_etc,
//               category_settings, visible_group_ids, visible_value_setting_ids
//   auto_schedule_log: user_id, task_id, task_title, scheduled_to, action,
//                      reviewed, created_at
//
// KNOWN, DELIBERATE DIVERGENCES FROM WEB (flagged to the caller in the
// final report — do not "fix" these back to match web without asking):
//
//  1. Priority formula: tasks/[listId]/page.tsx's calculateTaskPriorityLocal
//     includes `unscheduledTaskPriorityBonus` + `priorityAdd`; tasks/page.tsx's
//     own calculateTaskPriority (used only for its OWN initiateAutoScheduling
//     + handleForceRescheduleList calls) omits both. This looks like drift
//     between two copies of the same function on web, not an intentional
//     product difference — the fuller formula is what's shown to the user
//     everywhere else (priority badges/tooltips). This port uses ONE
//     calculateTaskPriority (the fuller formula) for every trigger.
//
//  2. Journal's "intelligent reschedule" (handleItineraryItemReschedule) on
//     web writes `{ scheduled: <date-only>, time: <hh:mm> }` and reads
//     `existingItem.etc` / `existingItem.time` for its overlap check — but
//     tasks has no `time` column (interfaces/settings.ts Task has none) and
//     CalendarDisplayItem has no `.time`/`.etc` field, only `.startTime`/
//     `.endTime` and `.originalData`. That web code path is dead-on-arrival:
//     the `time` write is silently dropped by Supabase (or errors) and the
//     overlap check always resolves to a zero-duration event, so it never
//     actually detects collisions. This port instead writes a single
//     combined `scheduled` datetime string (matching every other writer of
//     that column across the app) and computes real obstacle durations from
//     `etc` / event start-end, i.e. it preserves the *intent* rather than
//     the *bug*.
//
//  3. The list-scoped scheduler (initiateAutoSchedulingForList /
//     initiateAutoScheduling) only treats other already-scheduled TASKS as
//     obstacles — it never reads `calendar_events`. That is preserved as-is
//     here (not a bug — it's how both web implementations behave). Only
//     `rescheduleTaskToNextSlot` (the Journal-triggered per-task reschedule)
//     also treats non-recurring calendar events as obstacles, matching
//     web's `allCalendarItems` set used in that one code path.
// ─────────────────────────────────────────────────────────────────────────

import { supabase } from './supabase';

// ---------------------------------------------------------------------------
// Types (mirror src/interfaces/settings.ts)
// ---------------------------------------------------------------------------

export interface TimeBlock {
  id: string;
  day: string; // 'sunday' .. 'saturday'
  startTime: string; // 'HH:mm'
  endTime: string; // 'HH:mm'
}

export interface Group {
  id: string;
  title: string;
  groupNumber: number;
  parentId: string | null;
  isActive: boolean;
  scheduleActive?: boolean;
  scheduleTimeBlocks?: TimeBlock[];
}

export interface Criterion {
  id: string;
  title: string;
  score: number;
}

export interface ValueSetting {
  id: string;
  name: string;
  isActive: boolean;
  criteria: Criterion[];
}

export interface CategorySetting {
  includeInAutoScheduler?: boolean;
  includeInStats?: boolean;
  includeInAllTasks?: boolean;
  strikethrough?: boolean;
}

/** Subset of the `tasks` row shape the engine needs (camelCase, mirrors
 *  src/lib/task-utils.ts mapTaskRow). */
export interface SchedulableTask {
  id: string;
  title: string;
  etc: string | null;
  scheduled: string | null;
  scheduledManual: boolean;
  groupId: string | null;
  sg1Id: string | null;
  sg2Id: string | null;
  valueSelections: Record<string, string>;
  priorityAdd: number | null;
  category: string | null;
  parentTaskId: string | null;
  tabled: boolean;
  unlisted: boolean;
  order: number | null;
}

export interface AutoScheduleLogEntry {
  id: string;
  task_id: string;
  task_title: string;
  scheduled_to: string;
  action: string;
  reviewed: boolean;
  created_at: string;
}

export interface RunSummary {
  scheduled: number;
  total: number;
  notices: string[];
}

const TASKS_TABLE = 'tasks';
const TASK_LISTS_TABLE = 'task_lists';
const AUTO_LOG_TABLE = 'auto_schedule_log';
const CALENDAR_EVENTS_TABLE = 'calendar_events';

const MAX_SEARCH_DAYS = 60;
const STEP_MINUTES = 15;

// ---------------------------------------------------------------------------
// ETC parsing (mirrors src/app/calendar/page.tsx parseETCToMinutes exactly)
// ---------------------------------------------------------------------------

export function parseETCToMinutes(etcLabel: string | null | undefined): number | null {
  if (!etcLabel) return null;
  const cleaned = etcLabel.toLowerCase().trim();
  const numMatch = cleaned.match(/^(\d+(\.\d+)?)/);
  if (!numMatch) return null;
  const num = parseFloat(numMatch[1]);
  if (Number.isNaN(num)) return null;
  if (cleaned.includes('min')) return num;
  if (cleaned.includes('hour')) return num * 60;
  if (cleaned.includes('day')) return num * 8 * 60;
  if (num <= 180) return num;
  return null;
}

const DAY_NAME_TO_NUMBER: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

function mapDayStringToNumber(day: string): number {
  return DAY_NAME_TO_NUMBER[(day || '').toLowerCase()] ?? -1;
}

// ---------------------------------------------------------------------------
// Priority (mirrors calculateTaskPriorityLocal in tasks/[listId]/page.tsx —
// see divergence note #1 above)
// ---------------------------------------------------------------------------

export function calculateTaskPriority(
  task: Pick<SchedulableTask, 'groupId' | 'sg1Id' | 'sg2Id' | 'valueSelections' | 'scheduled' | 'priorityAdd'>,
  groups: Group[],
  valueSettings: ValueSetting[],
  unscheduledBonus: number
): number {
  let total = 0;
  const group = groups.find((g) => g.id === task.groupId);
  if (group) total += group.groupNumber;
  const sg1 = groups.find((g) => g.id === task.sg1Id);
  if (sg1) total += sg1.groupNumber;
  const sg2 = groups.find((g) => g.id === task.sg2Id);
  if (sg2) total += sg2.groupNumber;
  if (task.valueSelections) {
    const activeValues = valueSettings.filter((v) => v.isActive);
    for (const [valueId, criterionId] of Object.entries(task.valueSelections)) {
      const vs = activeValues.find((v) => v.id === valueId);
      if (!vs) continue;
      const crit = vs.criteria.find((c) => c.id === criterionId);
      if (crit) total += crit.score;
    }
  }
  if (!task.scheduled) total += unscheduledBonus;
  if (task.priorityAdd) total += task.priorityAdd;
  return total;
}

// ---------------------------------------------------------------------------
// Plain-JS date helpers (no date-fns anywhere in this app)
// ---------------------------------------------------------------------------

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function todayMidnight(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDaysToDate(d: Date, n: number): Date {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function atHHMM(day: Date, hhmm: string): Date {
  const [h, m] = hhmm.split(':').map(Number);
  const c = new Date(day);
  c.setHours(h || 0, m || 0, 0, 0);
  return c;
}

function scheduledDateTimeString(d: Date): string {
  return `${ymd(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** Parses a `scheduled` column value: full 'yyyy-MM-dd HH:mm' (or
 *  '...THH:mm'), a bare 'yyyy-MM-dd' (midnight), or 'today'/'tomorrow'/
 *  'yesterday'. Mirrors parseScheduledDateForSort in tasks/[listId]/page.tsx. */
export function parseScheduled(value: string | null | undefined): Date | null {
  if (!value) return null;
  const full = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(value);
  if (full) {
    return new Date(Number(full[1]), Number(full[2]) - 1, Number(full[3]), Number(full[4]), Number(full[5]));
  }
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]), 0, 0);
  }
  const lower = value.toLowerCase().trim();
  const today = todayMidnight();
  if (lower === 'today') return today;
  if (lower === 'tomorrow') return addDaysToDate(today, 1);
  if (lower === 'yesterday') return addDaysToDate(today, -1);
  return null;
}

interface Interval {
  start: number; // epoch ms
  end: number;
}

function overlaps(a: Interval, b: Interval): boolean {
  // Non-inclusive, matches date-fns areIntervalsOverlapping({ inclusive: false }).
  return a.start < b.end && b.start < a.end;
}

// ---------------------------------------------------------------------------
// Shared "prepare" step — validates the list default group, computes the
// longest available block, and splits over-long tasks into chunks. Mirrors
// the identical logic duplicated in both web functions.
// ---------------------------------------------------------------------------

interface PreparedEntry {
  /** Existing task id to UPDATE, or null when this is a brand-new chunk row
   *  that must be INSERTed first (chunkInsertPayload will be set). */
  taskId: string | null;
  title: string;
  etc: string;
  durationMinutes: number;
  priority: number;
  groupTimeBlocks: TimeBlock[];
  chunkInsertPayload?: Record<string, any>;
}

interface PrepareResult {
  prepared: PreparedEntry[];
  notices: string[];
}

function prepareEntries(
  tasks: SchedulableTask[],
  groups: Group[],
  valueSettings: ValueSetting[],
  unscheduledBonus: number,
  listDefaultGroupId: string | null | undefined,
  listDefaultETC: string | null | undefined,
  userId: string,
  listId: string
): PrepareResult | { error: string } {
  if (!listDefaultGroupId) {
    return { error: "This list has no default group set. Please configure one in List Settings." };
  }
  const listGroup = groups.find((g) => g.id === listDefaultGroupId);
  if (!listGroup || !listGroup.scheduleActive || !listGroup.scheduleTimeBlocks || listGroup.scheduleTimeBlocks.length === 0) {
    return {
      error: `The list's default group ("${listGroup?.title || listDefaultGroupId}") must be 'Schedule Active' and have time slots defined in Settings.`,
    };
  }
  const listTimeBlocks = listGroup.scheduleTimeBlocks;

  const longestBlockMinutes = listTimeBlocks.reduce((max, blk) => {
    const [sh, sm] = blk.startTime.split(':').map(Number);
    const [eh, em] = blk.endTime.split(':').map(Number);
    const len = eh * 60 + em - (sh * 60 + sm);
    return len > max ? len : max;
  }, 0);

  const prepared: PreparedEntry[] = [];
  const notices: string[] = [];
  const now = new Date().toISOString();

  for (const task of tasks) {
    const effectiveETC = task.etc || listDefaultETC;
    if (!effectiveETC) {
      notices.push(`Task "${task.title}" skipped: requires an ETC. Set a default ETC for the list or assign one to the task.`);
      continue;
    }
    const durationMinutes = parseETCToMinutes(effectiveETC);
    if (durationMinutes === null || durationMinutes <= 0) {
      notices.push(`Task "${task.title}" skipped: invalid or zero ETC duration ("${effectiveETC}").`);
      continue;
    }
    const priority = calculateTaskPriority(task, groups, valueSettings, unscheduledBonus);

    if (durationMinutes > longestBlockMinutes) {
      const numChunks = Math.ceil(durationMinutes / longestBlockMinutes);
      const rawChunk = durationMinutes / numChunks;
      const chunkMinutes = Math.min(longestBlockMinutes, Math.ceil(rawChunk / 15) * 15);
      const baseTitle = (task.title || '').trim();

      // First chunk reuses the original row.
      prepared.push({
        taskId: task.id,
        title: `${baseTitle} (1/${numChunks})`,
        etc: `${chunkMinutes} min`,
        durationMinutes: chunkMinutes,
        priority,
        groupTimeBlocks: listTimeBlocks,
      });
      // Remaining chunks are new rows, inserted only if a slot is found.
      for (let c = 2; c <= numChunks; c++) {
        const chunkPayload: Record<string, any> = {
          user_id: userId,
          list_id: listId,
          title: `${baseTitle} (${c}/${numChunks})`,
          done: false,
          value_selections: task.valueSelections || {},
          group_id: listDefaultGroupId,
          sg1_id: task.sg1Id ?? null,
          sg2_id: task.sg2Id ?? null,
          etc: `${chunkMinutes} min`,
          priority_add: task.priorityAdd ?? null,
          category: task.category ?? null,
          parent_task_id: task.parentTaskId ?? null,
          tabled: task.tabled ?? false,
          unlisted: task.unlisted ?? false,
          order: task.order ?? 0,
          scheduled_manual: false,
          created_at: now,
          updated_at: now,
        };
        prepared.push({
          taskId: null,
          title: chunkPayload.title,
          etc: chunkPayload.etc,
          durationMinutes: chunkMinutes,
          priority,
          groupTimeBlocks: listTimeBlocks,
          chunkInsertPayload: chunkPayload,
        });
      }
      notices.push(
        `Task "${baseTitle}" (${durationMinutes} min) exceeded the longest block (${longestBlockMinutes} min) — split into ${numChunks} chunks of ${chunkMinutes} min.`
      );
      continue;
    }

    prepared.push({
      taskId: task.id,
      title: task.title,
      etc: effectiveETC,
      durationMinutes,
      priority,
      groupTimeBlocks: listTimeBlocks,
    });
  }

  return { prepared, notices };
}

// ---------------------------------------------------------------------------
// Obstacle loading — every OTHER already-scheduled, undone task for this
// user (excluding the ids being rescheduled this run). Matches both web
// functions' "Pre-populate obstacles" step. Only tasks — never calendar
// events (see divergence note #3).
// ---------------------------------------------------------------------------

async function loadTaskObstacles(
  userId: string,
  excludeTaskIds: Set<string>,
  fallbackEtc: string | null | undefined
): Promise<Interval[]> {
  const { data, error } = await supabase
    .from(TASKS_TABLE)
    .select('id, etc, scheduled, done')
    .eq('user_id', userId)
    .eq('done', false)
    .not('scheduled', 'is', null);
  if (error || !data) return [];
  const out: Interval[] = [];
  for (const row of data as any[]) {
    if (!row.id || !row.scheduled) continue;
    if (excludeTaskIds.has(row.id)) continue;
    const etcValue: string | null = row.etc ?? fallbackEtc ?? null;
    const duration = etcValue ? parseETCToMinutes(etcValue) : null;
    if (!duration || duration <= 0) continue;
    const start = parseScheduled(row.scheduled);
    if (!start) continue;
    out.push({ start: start.getTime(), end: start.getTime() + duration * 60000 });
  }
  return out;
}

// ---------------------------------------------------------------------------
// The slot-search + commit step, shared by every list-scoped trigger.
// Mirrors the day/block/step loop duplicated in both web functions exactly:
//   - scan up to MAX_SEARCH_DAYS days starting at `startDayOffset`
//   - within each matching day-of-week time block, step by STEP_MINUTES
//   - on overlap, jump to the end of the conflicting slot (rounded up to
//     the next step boundary) instead of just advancing by one step
//   - first fit wins; sorted by priority descending beforehand
// ---------------------------------------------------------------------------

async function runScheduleAssignment(
  userId: string,
  entries: PreparedEntry[],
  obstacles: Interval[],
  startDayOffset: number,
  logAction: string
): Promise<{ scheduled: number }> {
  const sorted = entries.slice().sort((a, b) => b.priority - a.priority);
  const slotsThisRun: Interval[] = obstacles.slice();
  // PromiseLike: supabase query builders' .then() returns PromiseLike, and
  // Promise.all accepts it fine.
  const writes: PromiseLike<any>[] = [];
  const logEntries: Array<{ task_id: string; task_title: string; scheduled_to: string; action: string }> = [];
  let scheduledCount = 0;

  for (const entry of sorted) {
    let placed = false;
    for (let dayOffset = startDayOffset; dayOffset < startDayOffset + MAX_SEARCH_DAYS && !placed; dayOffset++) {
      const day = addDaysToDate(todayMidnight(), dayOffset);
      const dow = day.getDay();
      for (const block of entry.groupTimeBlocks) {
        if (placed) break;
        if (mapDayStringToNumber(block.day) !== dow) continue;
        let slotStart = atHHMM(day, block.startTime);
        const blockEnd = atHHMM(day, block.endTime);
        while (slotStart.getTime() < blockEnd.getTime()) {
          const slotEnd = new Date(slotStart.getTime() + entry.durationMinutes * 60000);
          if (slotEnd.getTime() > blockEnd.getTime()) break;

          const proposed: Interval = { start: slotStart.getTime(), end: slotEnd.getTime() };
          const conflict = slotsThisRun.find((s) => overlaps(proposed, s));
          if (conflict) {
            let next = new Date(conflict.end);
            const remainder = next.getMinutes() % STEP_MINUTES;
            if (remainder !== 0) next = new Date(next.getTime() + (STEP_MINUTES - remainder) * 60000);
            next.setSeconds(0, 0);
            slotStart = next;
            continue;
          }

          // Found a slot.
          const scheduledTo = scheduledDateTimeString(slotStart);
          if (entry.chunkInsertPayload) {
            const payload = { ...entry.chunkInsertPayload, scheduled: scheduledTo, updated_at: new Date().toISOString() };
            writes.push(
              supabase
                .from(TASKS_TABLE)
                .insert(payload)
                .then((res: any) => {
                  if (res?.error) console.error('[AutoSched] chunk insert failed:', res.error);
                })
            );
            logEntries.push({ task_id: '', task_title: entry.title, scheduled_to: scheduledTo, action: logAction });
          } else if (entry.taskId) {
            writes.push(
              supabase
                .from(TASKS_TABLE)
                .update({
                  title: entry.title,
                  etc: entry.etc,
                  scheduled: scheduledTo,
                  scheduled_manual: false,
                  updated_at: new Date().toISOString(),
                })
                .eq('id', entry.taskId)
                .eq('user_id', userId)
                .then((res: any) => {
                  if (res?.error) console.error('[AutoSched] update failed:', res.error);
                })
            );
            logEntries.push({ task_id: entry.taskId, task_title: entry.title, scheduled_to: scheduledTo, action: logAction });
          }
          slotsThisRun.push(proposed);
          scheduledCount++;
          placed = true;
          break;
        }
      }
    }

    if (!placed && entry.taskId) {
      // Couldn't place a real (non-chunk) task anywhere in the window —
      // clear it so it doesn't show a stale/incorrect date. Chunk rows that
      // can't be placed are simply never inserted (matches web).
      writes.push(
        supabase
          .from(TASKS_TABLE)
          .update({ scheduled: null, scheduled_manual: false, updated_at: new Date().toISOString() })
          .eq('id', entry.taskId)
          .eq('user_id', userId)
          .then((res: any) => {
            if (res?.error) console.error('[AutoSched] clear failed:', res.error);
          })
      );
    }
  }

  await Promise.all(writes);
  if (logEntries.length > 0) {
    const withUser = logEntries.filter((e) => e.task_id).map((e) => ({ ...e, user_id: userId }));
    if (withUser.length > 0) {
      const { error } = await supabase.from(AUTO_LOG_TABLE).insert(withUser);
      if (error) console.error('[AutoSched] log insert failed:', error);
    }
  }

  return { scheduled: scheduledCount };
}

// ---------------------------------------------------------------------------
// mapTaskRow-equivalent for engine-internal fetches (kept local so this file
// has no dependency on the tool files).
// ---------------------------------------------------------------------------

function mapSchedulable(row: any): SchedulableTask {
  return {
    id: row.id,
    title: row.title ?? '',
    etc: row.etc ?? null,
    scheduled: row.scheduled ?? null,
    scheduledManual: !!row.scheduled_manual,
    groupId: row.group_id ?? null,
    sg1Id: row.sg1_id ?? null,
    sg2Id: row.sg2_id ?? null,
    valueSelections: row.value_selections ?? {},
    priorityAdd: row.priority_add ?? null,
    category: row.category ?? null,
    parentTaskId: row.parent_task_id ?? null,
    tabled: !!row.tabled,
    unlisted: !!row.unlisted,
    order: typeof row.order === 'number' ? row.order : null,
  };
}

// ---------------------------------------------------------------------------
// Public entry point #1 — mirrors initiateAutoSchedulingForList
// (src/app/tasks/[listId]/page.tsx). Trigger: task add / done-toggle /
// delete inside a list that has auto-scheduler enabled. Always re-optimizes
// every eligible undone, non-manual task in the list (not just the one that
// changed).
// ---------------------------------------------------------------------------

export async function scheduleListNow(
  userId: string,
  listId: string,
  groups: Group[],
  valueSettings: ValueSetting[],
  unscheduledBonus: number
): Promise<RunSummary | { skipped: string } | { error: string }> {
  // Defense-in-depth gate — re-verify the list actually has auto-scheduler
  // enabled before touching any rows (matches web's own re-check).
  const { data: listRow, error: listErr } = await supabase
    .from(TASK_LISTS_TABLE)
    .select('id, auto_scheduler_enabled, default_group_id, default_etc, category_settings')
    .eq('id', listId)
    .eq('user_id', userId)
    .maybeSingle();
  if (listErr) return { error: listErr.message };
  if (!listRow || listRow.auto_scheduler_enabled !== true) {
    return { skipped: 'This list does not have auto-scheduler enabled.' };
  }

  const { data, error } = await supabase
    .from(TASKS_TABLE)
    .select('*')
    .eq('user_id', userId)
    .eq('list_id', listId)
    .eq('done', false);
  if (error) return { error: error.message };

  const catSettings: Record<string, CategorySetting> = listRow.category_settings ?? {};
  const tasks = (data ?? [])
    .map(mapSchedulable)
    .filter((t) => {
      if (t.unlisted) return false;
      if (t.scheduledManual) return false;
      if (t.category) return catSettings[t.category]?.includeInAutoScheduler ?? true;
      return true;
    });

  if (tasks.length === 0) return { scheduled: 0, total: 0, notices: [] };

  const prep = prepareEntries(
    tasks,
    groups,
    valueSettings,
    unscheduledBonus,
    listRow.default_group_id,
    listRow.default_etc,
    userId,
    listId
  );
  if ('error' in prep) return { error: prep.error };
  if (prep.prepared.length === 0) return { scheduled: 0, total: 0, notices: prep.notices };

  const excludeIds = new Set(tasks.map((t) => t.id));
  const obstacles = await loadTaskObstacles(userId, excludeIds, listRow.default_etc);
  const { scheduled } = await runScheduleAssignment(userId, prep.prepared, obstacles, 0, 'auto_scheduled');

  return { scheduled, total: prep.prepared.length, notices: prep.notices };
}

// ---------------------------------------------------------------------------
// Public entry point #2 — mirrors initiateAutoScheduling
// (src/app/tasks/page.tsx). Trigger: the per-list "Auto-Scheduler" switch
// turned ON, or internally by forceRescheduleList below. Only schedules
// tasks that are NOT already scheduled, unless an explicit `eligibleTaskIds`
// subset is given.
// ---------------------------------------------------------------------------

export async function scheduleListSubset(
  userId: string,
  listId: string,
  listDefaultGroupId: string | null,
  listDefaultETC: string | null,
  groups: Group[],
  valueSettings: ValueSetting[],
  unscheduledBonus: number,
  eligibleTaskIds?: Set<string>
): Promise<RunSummary | { error: string }> {
  const { data, error } = await supabase
    .from(TASKS_TABLE)
    .select('*')
    .eq('user_id', userId)
    .eq('list_id', listId)
    .eq('done', false);
  if (error) return { error: error.message };

  const allTasks = (data ?? []).map(mapSchedulable);
  const listedTasks = allTasks.filter((t) => !t.unlisted);

  const eligible = listedTasks.filter((t) => {
    if (t.scheduledManual) return false;
    if (eligibleTaskIds) return eligibleTaskIds.has(t.id);
    return !t.scheduled;
  });

  // Clear stale `scheduled` on unlisted tasks (matches web's cleanup pass).
  const unlistedWithSchedule = allTasks.filter((t) => t.unlisted && t.scheduled);
  if (unlistedWithSchedule.length > 0) {
    await supabase
      .from(TASKS_TABLE)
      .update({ scheduled: null, updated_at: new Date().toISOString() })
      .in(
        'id',
        unlistedWithSchedule.map((t) => t.id)
      )
      .eq('user_id', userId);
  }

  if (eligible.length === 0) return { scheduled: 0, total: 0, notices: [] };

  const prep = prepareEntries(eligible, groups, valueSettings, unscheduledBonus, listDefaultGroupId, listDefaultETC, userId, listId);
  if ('error' in prep) return { error: prep.error };
  if (prep.prepared.length === 0) return { scheduled: 0, total: 0, notices: prep.notices };

  const excludeIds = new Set(eligible.map((t) => t.id));
  const obstacles = await loadTaskObstacles(userId, excludeIds, listDefaultETC);
  const { scheduled } = await runScheduleAssignment(userId, prep.prepared, obstacles, 0, 'auto_scheduled');

  return { scheduled, total: prep.prepared.length, notices: prep.notices };
}

// ---------------------------------------------------------------------------
// Public entry point #3 — mirrors handleForceRescheduleList
// (src/app/tasks/page.tsx). Trigger: the "Reschedule" button in a list's
// Auto-Scheduler settings. Prunes ghost group/value references, clears
// `scheduled`/`scheduled_manual` on every eligible task, then re-schedules
// exactly that set from scratch.
// ---------------------------------------------------------------------------

export async function forceRescheduleList(
  userId: string,
  listId: string,
  groups: Group[],
  valueSettings: ValueSetting[],
  unscheduledBonus: number
): Promise<RunSummary | { error: string } | { skipped: string }> {
  const { data: listRow, error: listErr } = await supabase
    .from(TASK_LISTS_TABLE)
    .select('id, default_group_id, default_etc, visible_group_ids, visible_value_setting_ids, category_settings, auto_scheduler_enabled')
    .eq('id', listId)
    .eq('user_id', userId)
    .maybeSingle();
  if (listErr) return { error: listErr.message };
  if (!listRow) return { error: 'List not found.' };
  if (listRow.auto_scheduler_enabled !== true) return { skipped: 'Auto-scheduler is not enabled for this list.' };

  const { data, error } = await supabase
    .from(TASKS_TABLE)
    .select('*')
    .eq('user_id', userId)
    .eq('list_id', listId)
    .eq('done', false);
  if (error) return { error: error.message };

  const allTasks = (data ?? []).map(mapSchedulable);
  const catSettings: Record<string, CategorySetting> = listRow.category_settings ?? {};
  const eligible = allTasks.filter((t) => {
    if (t.unlisted) return false;
    if (t.scheduledManual) return false;
    if (t.category) {
      const cs = catSettings[t.category] || {};
      if (cs.includeInAutoScheduler === false) return false;
      if (cs.includeInStats === false) return false;
    }
    return true;
  });
  if (eligible.length === 0) return { scheduled: 0, total: 0, notices: [] };

  const visibleGroupIds: string[] | null = Array.isArray(listRow.visible_group_ids) ? listRow.visible_group_ids : null;
  const visibleValueSettingIds: string[] | null = Array.isArray(listRow.visible_value_setting_ids)
    ? listRow.visible_value_setting_ids
    : null;

  const groupIsUsable = (gid: string | null | undefined): boolean => {
    if (!gid) return false;
    if (!groups.find((g) => g.id === gid)) return false;
    if (visibleGroupIds && !visibleGroupIds.includes(gid)) return false;
    return true;
  };

  const now = new Date().toISOString();
  const pruneWrites = eligible.map((task) => {
    const patch: Record<string, any> = { scheduled: null, scheduled_manual: false, updated_at: now };
    if (task.groupId && !groupIsUsable(task.groupId)) {
      patch.group_id = null;
      patch.sg1_id = null;
      patch.sg2_id = null;
    } else {
      if (task.sg1Id && !groups.find((g) => g.id === task.sg1Id)) {
        patch.sg1_id = null;
        patch.sg2_id = null;
      } else if (task.sg2Id && !groups.find((g) => g.id === task.sg2Id)) {
        patch.sg2_id = null;
      }
    }
    if (task.valueSelections && Object.keys(task.valueSelections).length > 0) {
      const cleaned: Record<string, string> = {};
      for (const [valueId, criterionId] of Object.entries(task.valueSelections)) {
        const vs = valueSettings.find((v) => v.id === valueId);
        if (!vs) continue;
        if (visibleValueSettingIds && !visibleValueSettingIds.includes(valueId)) continue;
        const criterion = vs.criteria?.find((c) => c.id === criterionId);
        if (!criterion) continue;
        cleaned[valueId] = criterionId;
      }
      if (JSON.stringify(cleaned) !== JSON.stringify(task.valueSelections)) {
        patch.value_selections = cleaned;
      }
    }
    return supabase
      .from(TASKS_TABLE)
      .update(patch)
      .eq('id', task.id)
      .eq('user_id', userId)
      .then((res: any) => {
        if (res?.error) console.error('[Reschedule] prune update failed:', res.error);
      });
  });
  await Promise.all(pruneWrites);

  const eligibleTaskIds = new Set(eligible.map((t) => t.id));
  return scheduleListSubset(
    userId,
    listId,
    listRow.default_group_id ?? null,
    listRow.default_etc ?? null,
    groups,
    valueSettings,
    unscheduledBonus,
    eligibleTaskIds
  );
}

// ---------------------------------------------------------------------------
// Clears every undone task's `scheduled` value in a list — mirrors web's
// "Bug 3 fix" when the Auto-Scheduler switch is turned OFF.
// ---------------------------------------------------------------------------

export async function clearListSchedules(userId: string, listId: string): Promise<number> {
  const { data, error } = await supabase
    .from(TASKS_TABLE)
    .update({ scheduled: null, updated_at: new Date().toISOString() })
    .eq('user_id', userId)
    .eq('list_id', listId)
    .eq('done', false)
    .select('id');
  if (error) {
    console.warn('[AutoSched] Could not clear scheduled values on disable:', error.message);
    return 0;
  }
  return data?.length ?? 0;
}

// ---------------------------------------------------------------------------
// Whether a list's Auto-Scheduler prerequisites are met (mirrors web's
// isAutoSchedulerPrerequisitesMet effect in src/app/tasks/page.tsx): a
// default group that is Schedule Active with at least one time block, and a
// default ETC.
// ---------------------------------------------------------------------------

export function autoSchedulerPrerequisitesMet(
  defaultGroupId: string | null | undefined,
  defaultETC: string | null | undefined,
  groups: Group[]
): boolean {
  if (!defaultGroupId || !defaultETC) return false;
  const group = groups.find((g) => g.id === defaultGroupId);
  return !!(group?.scheduleActive && group?.scheduleTimeBlocks && group.scheduleTimeBlocks.length > 0);
}

// ---------------------------------------------------------------------------
// Public entry point #4 — mirrors handleItineraryItemReschedule's task
// branch (src/app/journal/page.tsx), corrected per divergence note #2:
// uses the TASK'S OWN group (not any list default), searches starting
// TOMORROW (dayOffset 1, not 0 — matches web), and treats both other
// scheduled tasks AND non-recurring calendar events as obstacles (matches
// web's `allCalendarItems`, which the list-scoped scheduler never reads).
// ---------------------------------------------------------------------------

export async function rescheduleTaskToNextSlot(
  userId: string,
  taskId: string,
  groups: Group[]
): Promise<
  | { status: 'scheduled'; scheduledTo: string; title: string }
  | { status: 'unscheduled'; reason: string; title: string }
  | { status: 'error'; message: string }
> {
  const { data: row, error } = await supabase.from(TASKS_TABLE).select('*').eq('id', taskId).eq('user_id', userId).maybeSingle();
  if (error) return { status: 'error', message: error.message };
  if (!row) return { status: 'error', message: 'Task not found.' };

  const task = mapSchedulable(row);
  const durationMinutes = parseETCToMinutes(task.etc);
  const groupId = task.groupId;

  if (!groupId || !durationMinutes || durationMinutes <= 0) {
    await supabase.from(TASKS_TABLE).update({ scheduled: null, updated_at: new Date().toISOString() }).eq('id', taskId).eq('user_id', userId);
    return { status: 'unscheduled', reason: 'it lacks a group or a valid ETC', title: task.title };
  }

  const group = groups.find((g) => g.id === groupId);
  if (!group || !group.scheduleActive || !group.scheduleTimeBlocks || group.scheduleTimeBlocks.length === 0) {
    await supabase.from(TASKS_TABLE).update({ scheduled: null, updated_at: new Date().toISOString() }).eq('id', taskId).eq('user_id', userId);
    return { status: 'unscheduled', reason: 'its group has no active schedule', title: task.title };
  }

  // Obstacles: other undone scheduled tasks (own ETC) + non-recurring
  // calendar events (matches web's RescheduleDialog `nonRecurringItems`
  // simplification for the same reason — recurring-event occurrence math
  // isn't needed anywhere else in the engine).
  const [otherTasksRes, eventsRes] = await Promise.all([
    supabase.from(TASKS_TABLE).select('id, etc, scheduled, done').eq('user_id', userId).eq('done', false).not('scheduled', 'is', null),
    supabase
      .from(CALENDAR_EVENTS_TABLE)
      .select('id, title, date, start_time, end_time, repeat')
      .eq('user_id', userId),
  ]);

  const taskObstaclesByDate = new Map<string, Interval[]>();
  for (const r of (otherTasksRes.data ?? []) as any[]) {
    if (!r.id || r.id === taskId || !r.scheduled) continue;
    const dur = parseETCToMinutes(r.etc);
    if (!dur || dur <= 0) continue;
    const start = parseScheduled(r.scheduled);
    if (!start) continue;
    const key = ymd(start);
    const arr = taskObstaclesByDate.get(key) ?? [];
    arr.push({ start: start.getTime(), end: start.getTime() + dur * 60000 });
    taskObstaclesByDate.set(key, arr);
  }
  const eventObstaclesByDate = new Map<string, Interval[]>();
  for (const e of (eventsRes.data ?? []) as any[]) {
    if (!e.title || !e.date || !e.start_time || !e.end_time) continue;
    if (e.repeat && e.repeat !== 'none') continue; // matches web's nonRecurringItems filter
    const start = atHHMM(new Date(`${e.date}T00:00:00`), e.start_time);
    let end = atHHMM(new Date(`${e.date}T00:00:00`), e.end_time);
    if (end.getTime() <= start.getTime()) end = new Date(end.getTime() + 24 * 60 * 60000);
    const arr = eventObstaclesByDate.get(e.date) ?? [];
    arr.push({ start: start.getTime(), end: end.getTime() });
    eventObstaclesByDate.set(e.date, arr);
  }

  for (let dayOffset = 1; dayOffset <= MAX_SEARCH_DAYS; dayOffset++) {
    const day = addDaysToDate(todayMidnight(), dayOffset);
    const dow = day.getDay();
    const dayKey = ymd(day);
    const dayObstacles = [...(taskObstaclesByDate.get(dayKey) ?? []), ...(eventObstaclesByDate.get(dayKey) ?? [])];

    for (const block of group.scheduleTimeBlocks) {
      if (mapDayStringToNumber(block.day) !== dow) continue;
      let slotStart = atHHMM(day, block.startTime);
      const blockEnd = atHHMM(day, block.endTime);
      while (slotStart.getTime() < blockEnd.getTime()) {
        const slotEnd = new Date(slotStart.getTime() + durationMinutes * 60000);
        if (slotEnd.getTime() > blockEnd.getTime()) break;
        const proposed: Interval = { start: slotStart.getTime(), end: slotEnd.getTime() };
        const conflict = dayObstacles.find((o) => overlaps(proposed, o));
        if (!conflict) {
          const scheduledTo = scheduledDateTimeString(slotStart);
          const { error: updErr } = await supabase
            .from(TASKS_TABLE)
            .update({ scheduled: scheduledTo, scheduled_manual: false, updated_at: new Date().toISOString() })
            .eq('id', taskId)
            .eq('user_id', userId);
          if (updErr) return { status: 'error', message: updErr.message };
          await supabase.from(AUTO_LOG_TABLE).insert({
            user_id: userId,
            task_id: taskId,
            task_title: task.title,
            scheduled_to: scheduledTo,
            action: 'auto_rescheduled',
          });
          return { status: 'scheduled', scheduledTo, title: task.title };
        }
        slotStart = new Date(slotStart.getTime() + STEP_MINUTES * 60000);
      }
    }
  }

  return { status: 'unscheduled', reason: `no available slot was found in the next ${MAX_SEARCH_DAYS} days`, title: task.title };
}

// ---------------------------------------------------------------------------
// Auto-schedule verification log helpers (mirrors the "bell" popover on
// both src/app/tasks/page.tsx and src/app/journal/page.tsx).
// ---------------------------------------------------------------------------

export async function fetchAutoScheduleLogs(userId: string): Promise<AutoScheduleLogEntry[]> {
  const { data, error } = await supabase
    .from(AUTO_LOG_TABLE)
    .select('*')
    .eq('user_id', userId)
    .eq('reviewed', false)
    .order('created_at', { ascending: false });
  if (error || !data) return [];
  return data as AutoScheduleLogEntry[];
}

/** Just the count of unreviewed logs — for callers (the SyQi home / FLO
 *  verification badge) that only need the number, not the rows. The full
 *  fetchAutoScheduleLogs pulls every unreviewed row with all columns (easily
 *  100s of rows / 100s of KB); a `head` count query returns no body, which
 *  keeps it off the home's critical load path. */
export async function countPendingAutoScheduleLogs(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from(AUTO_LOG_TABLE)
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('reviewed', false);
  if (error) return 0;
  return count ?? 0;
}

export async function keepAutoScheduleLog(logId: string): Promise<void> {
  await supabase.from(AUTO_LOG_TABLE).update({ reviewed: true }).eq('id', logId);
}

export async function keepAllAutoScheduleLogs(logIds: string[]): Promise<void> {
  if (logIds.length === 0) return;
  await supabase.from(AUTO_LOG_TABLE).update({ reviewed: true }).in('id', logIds);
}

/** User manually picks a new date/time for a logged entry from the
 *  verification list — mirrors handleLogRescheduleComplete on web: this is
 *  a MANUAL override, so `scheduled_manual` is set true and the
 *  auto-scheduler will never touch this task again until manually cleared. */
export async function manuallyRescheduleLoggedTask(
  log: AutoScheduleLogEntry,
  userId: string,
  scheduledTo: string
): Promise<void> {
  await supabase
    .from(TASKS_TABLE)
    .update({ scheduled: scheduledTo, scheduled_manual: true, updated_at: new Date().toISOString() })
    .eq('id', log.task_id)
    .eq('user_id', userId);
  await supabase.from(AUTO_LOG_TABLE).update({ reviewed: true }).eq('id', log.id);
}

export { scheduledDateTimeString };
